<template>
  <v-card>
    <v-card-title>
      <v-toolbar-title>{{ $i18n.translate("Serial Numbers") }}</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-form @submit.stop.prevent="onSearchSubmit">
        <v-text-field
          v-model="search"
          :label="$i18n.translate('Filter')"
          flat
          solo-inverted
          hide-details
          clearable
          clear-icon="mdi-close-circle-outline"
          @click:clear="onSearchClear"
        ></v-text-field>
      </v-form>
      <v-menu offset-y :close-on-content-click="false">
        <template v-slot:activator="{ on: onMenu }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on: onTooltip }">
              <v-btn class="mr-2" icon v-on="{ ...onMenu, ...onTooltip }">
                <v-icon>mdi-magnify</v-icon>
              </v-btn>
            </template>
            <span>{{ $i18n.translate("Advanced Search") }}</span>
          </v-tooltip>
        </template>
        <v-list>
          <v-list-item-group color="primary">
            <v-subheader>{{ $i18n.translate("Advanced Search") }}</v-subheader>
            <v-list-item selectable>
              <v-list-item-content>
                <v-select
                  dense
                  :items="effectiveItems"
                  v-model="filterByEffective"
                  :label="$i18n.translate('Active') + ' / ' + $i18n.translate('Inactive')"
                  clearable
                  @input="onSearchSubmit"
                ></v-select>
              </v-list-item-content>
            </v-list-item>
          </v-list-item-group>
        </v-list>
      </v-menu>
    </v-card-title>

    <v-data-table
      :server-items-length="total"
      :options.sync="options"
      :loading="loading"
      :headers="computedHeaders"
      :items="productSerialNumbers"
    >
      <template v-slot:item.productSerialNumberKey="{ item }">
        <a v-if="canRead" href="#" @click.prevent="selectProductSerialNumber(item)">
          {{ item.productSerialNumberKey }}
        </a>
        <template v-else>
          {{ item.productSerialNumberKey }}
        </template>
      </template>
      <template v-slot:item.expirationDate="{ item }">
        {{ item.expirationDate | formatDateClient("MM/DD/YYYY", selectedClient) }}
      </template>
      <template v-slot:item.effective="{ item }">
        <v-chip color="primary" v-if="item.effective">{{ $i18n.translate("Active") }}</v-chip>
        <v-chip color="danger" v-else>{{ $i18n.translate("Inactive") }}</v-chip>
      </template>
      <template v-slot:item.updatedDate="{ item }">
        {{ item.updatedDate | formatDateFromNow }}
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "ProductSerialNumbers",
  props: {
    productId: { type: [Number, String] }
  },
  data() {
    return {
      search: "",
      options: { page: 1, itemsPerPage: 10, sortBy: ["updatedDate"], sortDesc: [true] },
      productSerialNumbers: [],
      total: 0,
      loading: false,
      filterByEffective: true,
      effectiveItems: [
        { text: "Active", value: true },
        { text: "Inactive", value: false },
        { text: "All", value: null }
      ],
      productSerialNumberHeaders: [
        { value: "productSerialNumberKey", text: this.$i18n.translate("Product Serial Number Key") },
        { value: "expirationDate", text: this.$i18n.translate("Expiration Date"), sortable: true },
        { value: "effective", text: "Status", align: "center", sortable: false },
        { value: "updatedDate", text: this.$i18n.translate("Last Updated"), sortable: true }
      ]
    };
  },

  computed: {
    ...mapGetters(["selectedProgram", "selectedClient"]),

    canRead() {
      return this.$privilege.hasPrivilege("PRODUCT_SERIAL_NUMBER_READ");
    },

    computedHeaders() {
      if (this.productId) {
        return this.productSerialNumberHeaders;
      }

      const [serialNumberHeader, ...remainingHeaders] = this.productSerialNumberHeaders;

      return [
        serialNumberHeader,
        { value: "productName", text: this.$i18n.translate("Product Name") },
        ...remainingHeaders
      ];
    }
  },
  methods: {
    selectProductSerialNumber(item) {
      this.$emit("select-product-serial-number", item);
    },

    onSearchClear() {
      this.search = "";
      this.onSearchSubmit();
    },
    onSearchSubmit() {
      if (this.options.page !== 1) {
        this.options.page = 1;
      } else {
        this.fetchProductSerialNumberData();
      }
    },
    fetchProductSerialNumberData() {
      this.loading = true;

      let filters = {};
      if (this.productId) {
        filters.product = { id: this.productId };
      }

      if (this.search && this.search.length > 0) {
        filters.keyword = this.search;
      }
      filters.isEffective = this.filterByEffective;
      const { sortBy, sortDesc, page, itemsPerPage } = this.options;

      return this.$api
        .post(
          "/api/productSerialNumbers/search?size=" +
            itemsPerPage +
            "&page=" +
            (page - 1) +
            (sortBy && sortBy.length > 0
              ? "&sort=" + sortBy[0] + "," + ((sortDesc && sortDesc.length > 0) & sortDesc[0] ? "DESC" : "ASC")
              : ""),
          filters
        )
        .then(({ data }) => {
          this.loading = false;

          if (!this.productId) {
            data.content.forEach(sn => {
              sn.productName = sn.product?.name || "";
            });
          }

          this.total = data.totalElements;
          this.productSerialNumbers = data.content;
        })
        .catch(() => {
          this.loading = false;
        });
    }
  },
  watch: {
    options: {
      handler() {
        this.fetchProductSerialNumberData();
      },
      deep: true
    }
  }
};
</script>
